import {useUser} from '@clerk/clerk-react'
import {queryOptions, useQuery} from '@tanstack/react-query'
import {ApiError} from '@/lib/api/client'
import {getMe} from '@/lib/api/users'

// Query options for current user - requires clerkUserId for enabled check
export const currentUserQueryOptions = (clerkUserId: string | undefined) =>
	queryOptions({
		queryKey: ['currentUser', clerkUserId] as const,
		queryFn: async () => {
			if (!clerkUserId) {
				throw new Error('No authenticated user')
			}
			const response = await getMe()
			return response.data
		},
		enabled: !!clerkUserId,
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		// A 401 means "not linked to an org" (uninvited) — a settled answer, not a
		// transient failure. Fail fast so the root layout shows the no-access screen
		// immediately instead of retrying through ~7s of backoff (the global retry:3).
		retry: (count, error) =>
			!(error instanceof ApiError && error.status === 401) && count < 3,
	})

/**
 * Fetch the current authenticated user's database record
 * Uses Clerk's externalId to fetch from the database
 */
export function useCurrentUser() {
	const {user: clerkUser} = useUser()

	return useQuery(currentUserQueryOptions(clerkUser?.id))
}
